import { createServerFn } from "@tanstack/react-start";
import { setResponseHeader } from "@tanstack/react-start/server";
import { createClient } from "@supabase/supabase-js";
import { resolveLiveTheme, type LiveTheme } from "@/lib/themeSchedule";
import { FALLBACK_LIVE_THEME, resolveThemeFast } from "@/lib/siteThemeFast";

async function loadTheme(): Promise<LiveTheme | null> {
  const url = process.env['SUPABASE_URL'] || import.meta.env['VITE_SUPABASE_URL'];
  const key = process.env['SUPABASE_PUBLISHABLE_KEY'] || import.meta.env['VITE_SUPABASE_PUBLISHABLE_KEY'];
  if (!url || !key) throw new Error("theme: missing config");
  const client = createClient(url, key, {
    auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
    global: {
      fetch: (input, init) => {
        const headers = new Headers(init?.headers);
        if (key.startsWith("sb_publishable_") && headers.get("Authorization") === `Bearer ${key}`) headers.delete("Authorization");
        headers.set("apikey", key);
        return fetch(input, { ...init, headers, signal: AbortSignal.timeout(5000) });
      },
    },
  });
  // Two reads sent concurrently over one connection (single TLS handshake).
  return resolveLiveTheme(client);
}

/** Public theme fields only; never blocks first byte longer than ~350 ms. */
export const getInitialSiteTheme = createServerFn({ method: "GET" }).handler(async () => {
  const { theme, path, ms } = await resolveThemeFast(loadTheme);
  try {
    setResponseHeader("Server-Timing", `theme;dur=${ms.toFixed(1)};desc="${path}"`);
  } catch {
    /* header context unavailable (client call) */
  }
  return theme ?? (path === "fallback" ? FALLBACK_LIVE_THEME : null);
});
